(() => {
  if (customElements.get('for-you-products')) return;
  const sources = ['alternative', 'related', 'complementary', 'upsell'];

  class ForYouProducts extends HTMLElement {
    connectedCallback() {
      this.fallback ||= this.querySelector('.collection').cloneNode(true);
      this.cartIds ||= new Set(this.dataset.cartIds.split(',').filter(Boolean));
      this.onHistoryChange = () => this.refresh();
      document.addEventListener('recently-viewed:change', this.onHistoryChange);
      if (typeof subscribe === 'function') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, async () => {
          try {
            // Add-to-cart responses can contain only the added item, so read the full cart.
            const response = await fetch(`${window.Shopify.routes.root}cart.js`);
            if (!response.ok || !this.isConnected) return;
            const cart = await response.json();
            this.cartIds = new Set(cart.items.map((item) => String(item.product_id)));
            this.refresh();
          } catch { /* Keep the currently rendered cards when the cart is unavailable. */ }
        });
      }
      Promise.all([window.DeqalliRecentlyViewed?.ready, window.DeqalliStorefrontContext?.ready]).then(() => {
        if (this.isConnected) this.refresh();
      });
    }

    disconnectedCallback() {
      document.removeEventListener('recently-viewed:change', this.onHistoryChange);
      this.unsubscribeCart?.();
      this.observer?.disconnect();
      this.controller?.abort();
    }

    refresh() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.removeAttribute('aria-busy');
      const entries = window.DeqalliRecentlyViewed?.getEntries() || [];
      this.excluded = new Set([...entries.map((entry) => String(entry.id)), ...this.cartIds]);
      this.limit = Math.max(4, Math.min(12, Number(this.dataset.limit) || 8));
      const priorities = this.dataset.priorities.split(',').map(Number);
      this.sourceOrder = sources.filter((source, index) => priorities[index] > 0)
        .sort((a, b) => priorities[sources.indexOf(b)] - priorities[sources.indexOf(a)]);

      const fallback = this.fallback.cloneNode(true);
      fallback.querySelectorAll('[data-home-fallback-id]').forEach((card) => {
        if (this.excluded.has(card.dataset.homeFallbackId)) card.remove();
      });
      const showFallback = () => {
        this.querySelector('.collection').replaceWith(fallback);
        this.hidden = !fallback.querySelector('.product-grid > li');
        this.configureSlider();
      };
      if (window.Shopify?.designMode || !this.sourceOrder.length || !window.DeqalliRecentlyViewed?.isAllowed()) return showFallback();

      const seedLimit = Math.max(1, Math.min(3, Number(this.dataset.seedLimit) || 3));
      const seeds = entries.filter((entry) => !['heren', 'dames'].includes(entry.context) || entry.context === this.dataset.department)
        .slice(0, seedLimit);
      if (!seeds.length) return showFallback();
      // Keep a server-rendered fallback visible while recommendations load.
      const load = () => this.load(seeds);
      if (!('IntersectionObserver' in window)) return load();
      this.observer = new IntersectionObserver((records) => {
        if (!records.some((record) => record.isIntersecting)) return;
        this.observer.disconnect();
        load();
      }, { rootMargin: '1600px 0px' });
      this.observer.observe(this.closest('contextual-content') || this);
    }

    async fetchPool(url, signal, seed) {
      try {
        const response = await fetch(url, { signal });
        if (!response.ok) return null;
        const pool = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-home-pool]');
        if (!pool || (seed && pool.dataset.sourceId !== String(seed.id))) return null;
        if (['heren', 'dames'].includes(pool.dataset.sourceContext) && pool.dataset.sourceContext !== this.dataset.department) return null;
        return pool;
      } catch {
        return null;
      }
    }

    selectCards(pools) {
      const queues = this.sourceOrder.map((source) => {
        const lists = pools.map((pool) => [...pool.querySelectorAll(`template[data-source="${source}"]`)]);
        const queue = [];
        for (let round = 0; round < 3; round++) lists.forEach((list) => { if (list[round]) queue.push(list[round]); });
        return queue;
      });
      const selected = [];
      const seen = new Set(this.excluded);
      while (selected.length < this.limit && queues.some((queue) => queue.length)) {
        for (const queue of queues) {
          let candidate;
          do { candidate = queue.shift(); } while (candidate && !this.eligible(candidate, seen));
          if (!candidate) continue;
          selected.push(candidate);
          seen.add(candidate.dataset.productId);
          if (selected.length === this.limit) break;
        }
      }
      return selected;
    }

    eligible(candidate, seen) {
      const { productId, context } = candidate.dataset;
      return /^\d+$/.test(productId) && !seen.has(productId) &&
        (!['heren', 'dames'].includes(context) || context === this.dataset.department);
    }

    async load(seeds) {
      const controller = new AbortController();
      this.controller = controller;
      const timeout = setTimeout(() => controller.abort(), 10000);
      this.setAttribute('aria-busy', 'true');
      try {
        const results = await Promise.all(seeds.map(async (seed) => {
          const url = new URL(`${window.Shopify.routes.root}products/${encodeURIComponent(seed.handle)}`, location.origin);
          url.searchParams.set('section_id', this.dataset.sectionId);
          return { seed, pool: await this.fetchPool(url, controller.signal, seed) };
        }));
        const usable = results.filter((result) => result.pool);
        const selected = this.selectCards(usable.map((result) => result.pool));
        const seen = new Set([...this.excluded, ...selected.map((card) => card.dataset.productId)]);
        if (selected.length < this.limit && this.dataset.apiFallback === 'true' && usable.length && !controller.signal.aborted) {
          const url = new URL(this.dataset.recommendationsUrl, location.origin);
          url.searchParams.set('section_id', this.dataset.sectionId);
          url.searchParams.set('product_id', usable[0].seed.id);
          url.searchParams.set('intent', 'related');
          url.searchParams.set('limit', '10');
          const pool = await this.fetchPool(url, controller.signal);
          for (const card of pool?.querySelectorAll('template[data-home-card]') || []) {
            if (selected.length >= this.limit) break;
            if (!this.eligible(card, seen)) continue;
            seen.add(card.dataset.productId);
            selected.push(card);
          }
        }
        if (!this.isConnected || this.controller !== controller || !window.DeqalliRecentlyViewed?.isAllowed()) return;
        const list = this.querySelector('.product-grid');
        const fragment = document.createDocumentFragment();
        selected.forEach((card) => {
          const item = document.createElement('li');
          item.className = 'grid__item';
          item.dataset.homeProductId = card.dataset.productId;
          item.append(document.importNode(card.content, true));
          fragment.append(item);
        });
        // Fill a short personalized row with the curated department selection.
        for (const card of this.fallback.querySelectorAll('[data-home-fallback-id]')) {
          if (fragment.children.length >= this.limit) break;
          if (seen.has(card.dataset.homeFallbackId)) continue;
          seen.add(card.dataset.homeFallbackId);
          fragment.append(card.cloneNode(true));
        }
        const visibleCount = window.matchMedia('(min-width: 990px)').matches
          ? Number(this.dataset.columnsDesktop) || 6 : (Number(this.dataset.columnsMobile) || 2) + 1;
        const prepared = await prepareProductCardImages(fragment, visibleCount, controller.signal);
        if (!prepared || !this.isConnected || this.controller !== controller || !window.DeqalliRecentlyViewed?.isAllowed()) return;
        list.replaceChildren(fragment);
        this.querySelector('[data-home-heading]').textContent = selected.length
          ? this.dataset.heading : this.fallback.querySelector('[data-home-heading]').textContent;
        this.hidden = !list.children.length;
        this.configureSlider();
      } finally {
        clearTimeout(timeout);
        if (this.controller === controller) this.removeAttribute('aria-busy');
      }
    }

    configureSlider() {
      const list = this.querySelector('.product-grid');
      const count = list.children.length;
      const mobile = this.dataset.mobileSlider === 'true' && count > Number(this.dataset.columnsMobile);
      const desktop = this.dataset.desktopSlider === 'true' && count > Number(this.dataset.columnsDesktop);
      const fullWidth = this.dataset.fullWidth === 'true';
      const slider = this.querySelector('slider-component');
      const title = this.querySelector('.collection__title');
      title.classList.toggle('title-wrapper--self-padded-tablet-down', mobile);
      title.classList.toggle('collection__title--desktop-slider', desktop);
      slider.classList.toggle('page-width', !mobile);
      slider.classList.toggle('page-width-desktop', !desktop && !fullWidth);
      slider.classList.toggle('slider-component-desktop', desktop);
      list.classList.toggle('slider', mobile || desktop);
      list.classList.toggle('slider--tablet', mobile);
      list.classList.toggle('slider--desktop', desktop);
      list.classList.toggle('grid--peek', mobile);
      [...list.children].forEach((item, index) => {
        item.id = `Slide-${this.dataset.sectionId}-${this.dataset.department}-${index + 1}`;
        item.classList.toggle('slider__slide', mobile || desktop);
      });
      this.querySelector('.slider-buttons').classList.toggle('hidden', this.dataset.pagination !== 'true' || !(mobile || desktop));
      slider.resetPages?.();
    }
  }

  customElements.define('for-you-products', ForYouProducts);
})();
